import { Injectable, signal, computed } from '@angular/core';

export interface FilterableItem {
    [key: string]: any;
}

export interface FilterConfig {
    searchFields: string[];
    placeholder: string;
    noResultsMessage: string;
    noDataMessage: string;
}

@Injectable({
    providedIn: 'root'
})
export class FilteringService {
    // Signals for state management
    private searchTextSignal = signal<string>('');
    private allItemsSignal = signal<FilterableItem[]>([]);
    private searchFieldsSignal = signal<string[]>([]);

    // Public readonly signals
    readonly searchText = this.searchTextSignal.asReadonly();
    readonly allItems = this.allItemsSignal.asReadonly();

    // Computed signal for filtered items
    readonly filteredItems = computed(() => {
        const items = this.allItemsSignal();
        const searchText = this.searchTextSignal();
        const searchFields = this.searchFieldsSignal();

        if (!searchText || !searchText.trim() || searchFields.length === 0) {
            return items;
        }

        return this.filterItems(items, searchText, searchFields);
    });

    constructor() { }

    /**
     * Filters items based on search text and specified fields
     */
    filterItems<T extends FilterableItem>(
        items: T[],
        searchText: string,
        searchFields: string[]
    ): T[] {
        if (!searchText || !searchText.trim()) {
            return items;
        }

        const searchTextLower = searchText.toLowerCase().trim();

        return items.filter(item =>
            searchFields.some(field => {
                const value = this.getNestedValue(item, field);
                if (value === null || value === undefined) {
                    return false;
                }

                // Convert to string and check if it contains the search text
                const stringValue = value.toString().toLowerCase();
                return stringValue.includes(searchTextLower);
            })
        );
    }

    /**
     * Gets nested property value using dot notation (e.g., 'player.name')
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : null;
        }, obj);
    }

    /**
     * Sets items and search fields for filtering
     */
    setItems(items: FilterableItem[], config: FilterConfig): void {
        this.allItemsSignal.set(items);
        this.searchFieldsSignal.set(config.searchFields);
    }

    /**
     * Updates search text
     */
    setSearchText(searchText: string): void {
        this.searchTextSignal.set(searchText);
    }

    /**
     * Updates the full dataset
     */
    updateAllItems(items: FilterableItem[]): void {
        this.allItemsSignal.set(items);
    }

    /**
     * Clears the search text
     */
    clearSearch(): void {
        this.searchTextSignal.set('');
    }

    /**
     * Gets current search text value
     */
    getCurrentSearchText(): string {
        return this.searchTextSignal();
    }

    /**
     * Gets current filtered items
     */
    getCurrentFilteredItems(): FilterableItem[] {
        return this.filteredItems();
    }

    /**
     * Gets current all items
     */
    getCurrentAllItems(): FilterableItem[] {
        return this.allItemsSignal();
    }
}
